import { BinaryReader } from "@/utility/binary/BinaryReader";
import { GameFileSystem } from "@/utility/GameFileSystem";
import { BinaryWriter } from "@/utility/binary/BinaryWriter";

/**
 * TwoDAObject class.
 * 
 * Class representing a 2D Array file in memory.
 * 
 * KotOR JS - A remake of the Odyssey Game Engine that powered KotOR I & II
 * 
 * @file TwoDAObject.ts
 * @author KobaltBlu <https://github.com/KobaltBlu>
 * @license {@link https://www.gnu.org/licenses/gpl-3.0.txt|GPLv3}
 */
export class TwoDAObject {

  file: Uint8Array|string|undefined = undefined;
  FileType: string;
  FileVersion: string;
  ColumnCount: number;
  RowCount: number;
  CellCount: number;
  columns: string[];
  rows: any = {};

  /**
   * Constructor for the TwoDAObject class
   * @param file - The file to read from
   * @param onComplete - The function to call when the 2DA object is loaded
   */
  constructor(file: Uint8Array|string|undefined = undefined, onComplete?: Function){
    this.file = file;
    this.columns = ["__rowlabel"];
    this.ColumnCount = 0;
    this.CellCount = 0;
    this.rows = {};
    
    if(!!file){
      if(file instanceof Uint8Array) {
        let br = new BinaryReader(file);
        this.read2DA(br);

        if(onComplete != null)
          onComplete();
      }else if(typeof file === "string"){
        this.file = file;
        GameFileSystem.readFile(this.file).then((buffer) => {
          let br = new BinaryReader(buffer);
          this.read2DA(br);

          if(onComplete != null)
            onComplete();
        }).catch((err) => {
          throw err;
        });
      }else{
        //invalid resource
      }
    }else{
      //invalid resource
    }
  }

  /**
   * Read the 2DA object from a binary reader
   * @param br - The binary reader to read from
   */
  read2DA(br: BinaryReader): void {
    this.FileType = br.readChars(4);
    this.FileVersion = br.readChars(4);

    br.position += 1; //0x0A = Newline (Skip)

    let str = "";
    let ch;
    this.columns = ["__rowlabel"];
    while ((ch = br.readChar()).charCodeAt(0) != 0){
      if(ch.charCodeAt(0) != 9){
        str = str + ch;
      }else{
        this.columns.push(str);
        str = '';
      }
    }

    this.ColumnCount = this.columns.length - 1;
    this.RowCount = br.readUInt32();

    //Get the row index numbers
    let RowIndexes = [];
    for (let i = 0; i < this.RowCount; i++){
      let rowIndex = "";
      let c;

      while ((c = br.readChar()).charCodeAt(0) != 9){
        rowIndex = rowIndex + c;
      }

      RowIndexes[i] = (rowIndex);
    }

    //Get the Row Data Offsets
    this.CellCount = this.ColumnCount * this.RowCount;
    let offsets = [];
    for (let i = 0; i < this.CellCount; i++){
      offsets[i] = br.readUInt16();
    }

    const dataSize = br.readUInt16();
    let dataOffset = br.position;

    //Get the Row Data
    for (let i = 0; i < this.RowCount; i++){

      let row: any = {"__index": i, "__rowlabel": RowIndexes[i] };

      for (let j = 0; j < this.ColumnCount; j++){

        let offset = dataOffset + offsets[i * this.ColumnCount + j];

        try{
          br.position = offset;
        }catch(e){
          console.error(e);
          throw e;
        }

        let token = "";
        let c;

        while((c = br.readChar()).charCodeAt(0) != 0)
          token = token + c;

        if(token == "")
          token = "****";

        row[this.columns[j+1]] = token;
      }

      this.rows[ i ] = row;

    }

  }

  /**
   * Convert the 2DA object to a buffer
   * @returns The buffer
   */
  toExportBuffer(): Uint8Array {
    try{
      const bw = new BinaryWriter();
      bw.writeChars('2DA ');
      bw.writeChars('V2.b');
      bw.writeByte(0x0A);//NewLine

      for(let i = 1; i < this.columns.length; i++){
        bw.writeChars(this.columns[i]);
        bw.writeByte(0x09); //HT Delineate Column Entry 
      }

      bw.writeByte(0x00); //Null Terminate Columns List

      const indexes = Object.keys(this.rows);
      //Write the row count as a UInt32
      bw.writeUInt32(indexes.length);

      for(let i = 0; i < indexes.length; i++){
        bw.writeChars(indexes[i]);
        bw.writeByte(0x09); //HT Delineate Row Index Entry 
      }

      const valuesWriter = new BinaryWriter();
      const values = new Map<string, number>(); //value, offset
      // values.set('Some Value', 0);
      for(let i = 0; i < indexes.length; i++){
        const index = indexes[i];
        const row = this.rows[index];
        const rowKeys = Object.keys(row);
        for(let j = 0; j < rowKeys.length; j++){
          const key = rowKeys[j];
          if(key != '__rowlabel' && key != '__index'){
            const value: string = row[key] == '****' ? '' : String(row[key]);
            if(values.has(value)){
              bw.writeUInt16(values.get(value));
            }else{
              const offset = valuesWriter.position;
              bw.writeUInt16(offset);
              valuesWriter.writeStringNullTerminated(value);
              values.set(value, offset);
            }
          }
        }
      }

      bw.writeUInt16(valuesWriter.buffer.length);
      bw.writeBytes(valuesWriter.buffer);

      return bw.buffer;
    }catch(e){
      console.error(e);
      return new Uint8Array(0);
    }
  }

  /**
   * Convert the 2DA object to a CSV string
   * @returns The CSV string
   */
  toCSV(): string {
    const quoteIfNeeded = (v: any): string => {
      if(v == null) return '';
      const s = String(v);
      if(s.includes(',') || s.includes('"') || s.includes('\n') || s.includes('\r')){
        return '"' + s.replace(/"/g, '""') + '"';
      }
      return s;
    };

    let csv = '';
    for(let i = 0; i < this.columns.length; i++){
      csv += quoteIfNeeded(this.columns[i]);
      if(i < this.columns.length - 1) csv += ',';
    }
    csv += '\n';
    const indexes = Object.keys(this.rows);
    for(let i = 0; i < indexes.length; i++){
      const index = indexes[i];
      const row = this.rows[index];
      for(let j = 0; j < this.columns.length; j++){
        csv += quoteIfNeeded(row[this.columns[j]]);
        if(j < this.columns.length - 1) csv += ',';
      }
      csv += '\n';
    }
    return csv;
  }

  /**
   * Parse a CSV string and populate this instance's columns and rows.
   * Handles RFC-4180 quoting (values containing commas or double-quotes).
   * @param csv - The CSV string to parse
   * @param options - Optional delimiter and header-row behavior
   * @returns A new TwoDAObject populated from the CSV data
   */
  static fromCSV(
    csv: string,
    options?: { delimiter?: string; hasHeader?: boolean },
  ): TwoDAObject {
    const obj = new TwoDAObject(undefined);
    const delimiter = options?.delimiter ?? ',';
    const hasHeader = options?.hasHeader !== false;

    const parseCSVLine = (line: string): string[] => {
      const result: string[] = [];
      let current = '';
      let inQuotes = false;
      for(let i = 0; i < line.length; i++){
        const ch = line[i];
        if(ch === '"'){
          if(inQuotes && i + 1 < line.length && line[i + 1] === '"'){
            current += '"';
            i++;
          }else{
            inQuotes = !inQuotes;
          }
        }else if(ch === delimiter && !inQuotes){
          result.push(current);
          current = '';
        }else{
          current += ch;
        }
      }
      result.push(current);
      return result;
    };

    // Normalise line endings
    const lines = csv.split('\n').map(l => l.endsWith('\r') ? l.slice(0, -1) : l);
    let lineIdx = 0;

    while(lineIdx < lines.length && lines[lineIdx].trim() === '') lineIdx++;
    if(lineIdx >= lines.length) return obj;

    let headers: string[];
    if(hasHeader){
      headers = parseCSVLine(lines[lineIdx++]);
    }else{
      const first = parseCSVLine(lines[lineIdx]);
      headers = first.map((_, i) => (i === 0 ? '__rowlabel' : `Column${i}`));
    }
    if(!headers.length || headers[0] !== '__rowlabel'){
      // Ensure a row-label column exists for editor compatibility
      if(headers[0] !== '__rowlabel'){
        headers = ['__rowlabel', ...headers];
      }
    }
    obj.columns = headers;
    obj.ColumnCount = Math.max(0, headers.length - 1);

    let rowIdx = 0;
    while(lineIdx < lines.length){
      const line = lines[lineIdx++];
      if(line.trim() === '') continue;
      const cells = parseCSVLine(line);
      const row: any = { __index: rowIdx };
      // When no header and we injected __rowlabel, shift cells so first CSV cell is __rowlabel
      const offset = (!hasHeader && headers[0] === '__rowlabel' && cells.length === headers.length - 1) ? 1 : 0;
      if(offset === 1){
        row['__rowlabel'] = String(rowIdx);
        for(let j = 1; j < headers.length; j++){
          row[headers[j]] = cells[j - 1] !== undefined ? cells[j - 1] : '****';
        }
      }else{
        for(let j = 0; j < headers.length; j++){
          row[headers[j]] = cells[j] !== undefined ? cells[j] : '****';
        }
        if(row['__rowlabel'] == null || row['__rowlabel'] === ''){
          row['__rowlabel'] = String(rowIdx);
        }
      }
      obj.rows[rowIdx] = row;
      rowIdx++;
    }

    obj.RowCount = rowIdx;
    obj.CellCount = obj.ColumnCount * obj.RowCount;
    return obj;
  }

  /**
   * Get a row by index
   * @param index - The index to get the row by
   * @returns The row
   */
  getRowByIndex(index = -1){
    for (let key of Object.keys(this.rows)) {
      if(this.rows[key]['__index'] == index){
        return this.rows[key];
      }
    }
  }

  /**
   * Get a row by ID
   * @param index - The ID to get the row by
   * @returns The row
   */
  getByID(index = -1){
    for (let key of Object.keys(this.rows)) {
      if(this.rows[key]['__rowlabel'] == index){
        return this.rows[key];
      }
    }
  }

  /**
   * Get a row by column and value
   * @param column - The column to get the row by
   * @param value - The value to get the row by
   * @returns The row
   */
  getRowByColumnAndValue(column: string = '', value: any = undefined){
    for (let key of Object.keys(this.rows)) {
      if(this.rows[key][column] == value){
        return this.rows[key];
      }
    }
  }

  /**
   * Parse a cell value
   * @param cell - The cell value to parse
   * @returns The parsed value
   */
  static cellParser(cell: any){
    if(cell === '****'){
      return null;
    }else{
      return cell;
    }
  }

  /**
   * Normalize a value based on the datatype
   * @param value - The value to normalize
   * @param datatype - The datatype to normalize to
   * @param default_value - The default value to return if the value is null
   * @returns The normalized value
   */
  static normalizeValue(value: any, datatype: 'number'|'string'|'boolean', default_value: any){
    switch(datatype){
      case 'number':
        if(typeof default_value === 'undefined') default_value = 0;
        if(value === '****') return default_value;

        if(typeof value === 'string' && value.slice(0, 2) == '0x'){
          return parseInt(value);
        }
        
        value = parseFloat(value);
        if(isNaN(value)) value = default_value;
        return value;
      break;
      case 'string':
        if(typeof default_value === 'undefined') default_value = '';
        if(value === '****') return default_value;
        return value;
      break;
      case 'boolean':
        if(typeof default_value === 'undefined') default_value = false;
        if(value === '****') return default_value;
        return !!value;
      break;
    }
    console.warn('normalizeValue', 'unhandled datatype', value);
    return '';
  }

}
